/**
 * RiskIndicatorsSection — surfaces the Risk Indicators data model for an
 * applicant. Per the Risk Intelligence Philosophy, indicators are *observations
 * that may warrant review* — never accusations.
 *
 * Mounted on Applicant Profile (always) and rolled up on Household Profile.
 *
 * Backend:
 *   GET    /api/screenings/{sid}/risk-indicators
 *   POST   /api/screenings/{sid}/risk-indicators   (staff)
 *   PATCH  /api/screenings/{sid}/risk-indicators/{rid}   (staff)
 *   DELETE /api/screenings/{sid}/risk-indicators/{rid}   (staff)
 */
import { useEffect, useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ShieldAlert, Plus, Pencil, Trash2, Check, X, Eye, MessageSquarePlus, ShieldCheck, MinusCircle, ChevronDown, ChevronUp, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import ManualSeverityPencil from "@/components/ManualSeverityPencil";
import { fmtDate } from "@/lib/dateFormat";

const STATUS_TONE = {
  open:          { label: "Open",         cls: "bg-amber-50 text-amber-700 border-amber-200",         accent: "border-l-amber-500" },
  reviewed:      { label: "Reviewed",     cls: "bg-blue-50 text-blue-700 border-blue-200",            accent: "border-l-blue-500" },
  under_review:  { label: "Under review", cls: "bg-blue-50 text-blue-700 border-blue-200",            accent: "border-l-blue-500" },
  resolved:      { label: "Resolved",     cls: "bg-emerald-50 text-emerald-700 border-emerald-200",   accent: "border-l-emerald-500" },
  dismissed:     { label: "Dismissed",    cls: "bg-slate-100 text-slate-600 border-slate-200",        accent: "border-l-slate-400" },
};

// Severity ordering — mirrored server-side. Elevated is reserved for confirmed
// or highly credible fraud, identity issues, fabricated references, or altered
// documents. Significant = material inconsistencies warranting heightened
// scrutiny but not conclusive of fraud. Potential = minor items to review.
const SEVERITY_TONE = {
  potential:   { label: "Potential",   cls: "bg-amber-50 text-amber-800 ring-amber-200" },
  significant: { label: "Significant", cls: "bg-orange-50 text-orange-800 ring-orange-200" },
  elevated:    { label: "Elevated",    cls: "bg-rose-50 text-rose-700 ring-rose-200" },
};
const SEV_RANK = { potential: 1, significant: 2, elevated: 3 };
const OPEN_STATUSES_SET = new Set(["open", "under_review", "reviewed"]);

function maxOpenSeverity(items) {
  let best = null;
  let bestRank = 0;
  for (const ri of items || []) {
    if (!OPEN_STATUSES_SET.has(ri.status)) continue;
    const rank = SEV_RANK[ri.severity] || 0;
    if (rank > bestRank) { bestRank = rank; best = ri.severity; }
  }
  return best;
}

export default function RiskIndicatorsSection({ screeningId, isStaff, applicantRisk, onApplicantRiskSaved }) {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  // Phase E.1 (2026-02): the headline is now the manually-selected value.
  // Indicators keep populating below as operational observations, but they
  // no longer set the top-line "Risk Intelligence" summary a PM reads.
  const [manual, setManual] = useState(applicantRisk || null);
  useEffect(() => { setManual(applicantRisk || null); }, [applicantRisk]);

  const load = async () => {
    try {
      const { data } = await apiClient.get(`/screenings/${screeningId}/risk-indicators`);
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load risk indicators");
    }
  };
  useEffect(() => { load(); }, [screeningId]); // eslint-disable-line

  // Severity-driven Risk Intelligence Summary — Phase E.1 prefers the
  // manually-set applicant risk. Operational indicators no longer drive
  // this. Missing manual value → "Pending NTN Review" (slate).
  const manualSev = manual?.severity && manual.severity !== "none" ? manual.severity : null;
  const riskHeadline = manual?.label
    || (manual?.severity === "none" ? "No Risk Indicators Found" : "Pending NTN Review");

  const HEAD_TONE = {
    pending:     { headerBg: "bg-slate-50/60 border-slate-200",     iconBg: "bg-slate-100 ring-slate-200",  iconFg: "text-slate-600",    count: "bg-white border-slate-200 text-slate-700" },
    none:        { headerBg: "bg-emerald-50/40 border-emerald-100", iconBg: "bg-emerald-50 ring-emerald-200", iconFg: "text-emerald-700",  count: "bg-white border-emerald-200 text-emerald-800" },
    potential:   { headerBg: "bg-amber-50/40 border-amber-100",     iconBg: "bg-amber-50 ring-amber-200",     iconFg: "text-amber-700",    count: "bg-white border-amber-200 text-amber-800" },
    significant: { headerBg: "bg-orange-50/40 border-orange-100",   iconBg: "bg-orange-50 ring-orange-200",   iconFg: "text-orange-700",   count: "bg-white border-orange-200 text-orange-800" },
    elevated:    { headerBg: "bg-rose-50/40 border-rose-100",       iconBg: "bg-rose-50 ring-rose-200",       iconFg: "text-rose-700",     count: "bg-white border-rose-200 text-rose-800" },
  };
  const headKey = manual?.severity || "pending";
  const head = HEAD_TONE[headKey] || HEAD_TONE.pending;

  return (
    <section className="bg-white rounded-lg ring-1 ring-slate-200 shadow-sm overflow-hidden" data-testid="risk-indicators-section">
      <header className={`flex items-center justify-between gap-3 px-5 py-3.5 border-b ${head.headerBg}`}>
        <div className="flex items-start gap-2.5 min-w-0">
          <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ring-1 ${head.iconBg}`}>
            {headKey === "pending"
              ? <HelpCircle className={`h-4 w-4 ${head.iconFg}`} />
              : manualSev
                ? <ShieldAlert className={`h-4 w-4 ${head.iconFg}`} />
                : <ShieldCheck className={`h-4 w-4 ${head.iconFg}`} />}
          </div>
          <div className="min-w-0">
            <div className="ntn-eyebrow flex items-center gap-2 flex-wrap">
              Risk Intelligence
              {isStaff && (
                <ManualSeverityPencil
                  endpoint={`/staff-ops/case/${screeningId}/applicant-risk`}
                  current={manual}
                  onSaved={(v) => { setManual(v); onApplicantRiskSaved?.(v); }}
                  testid="applicant-risk-pencil"
                />
              )}
            </div>
            <div className="ntn-h3 mt-0.5" data-testid="risk-indicators-headline">
              {riskHeadline}
            </div>
            <div className="ntn-caption mt-0.5">
              {headKey === "pending"
                ? "NTN staff will assign a risk value once the case is reviewed."
                : items.length === 0
                  ? "NTN has not flagged any observations for this application."
                  : "Observations that may warrant additional review — not conclusions."}
            </div>
            {manual?.set_by_name && manual?.set_at && (
              <div className="text-[10px] text-slate-500 font-mono mt-1" data-testid="applicant-risk-attribution">
                Set by {manual.set_by_name} · {fmtDate(manual.set_at)}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {items.length > 0 && (
            <Badge variant="outline" className={`text-[10px] uppercase tracking-wider font-bold ${head.count}`} data-testid="risk-indicators-count-badge">
              {items.length}
            </Badge>
          )}
          {isStaff && !adding && (
            <Button size="sm" className="bg-slate-900 hover:bg-slate-800 h-8 rounded-md" onClick={() => setAdding(true)} data-testid="risk-indicator-add-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add
            </Button>
          )}
        </div>
      </header>

      <div className="p-5 space-y-3">
        {adding && (
          <AddForm screeningId={screeningId} onClose={() => setAdding(false)} onAdded={() => { setAdding(false); load(); }} />
        )}

        {items.length === 0 && !adding ? (
          <div className="py-8 text-center" data-testid="risk-indicators-empty">
            <div className="inline-flex h-14 w-14 rounded-full bg-emerald-50 ring-2 ring-emerald-100 items-center justify-center mb-3">
              <ShieldCheck className="h-6 w-6 text-emerald-600" />
            </div>
            <div className="font-serif text-lg text-slate-900">Clean Profile</div>
            <div className="text-sm text-slate-500 mt-1.5 max-w-sm mx-auto">
              No risk indicators have been identified during NTN verification of this application.
            </div>
          </div>
        ) : (
          <ul className="space-y-4" data-testid="risk-indicators-list">
            {items.map((r) => (
              <RiskIndicatorRow key={r.id} indicator={r} screeningId={screeningId} isStaff={isStaff} onChanged={load} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function RiskIndicatorRow({ indicator, screeningId, isStaff, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [actionForm, setActionForm] = useState(null);  // 'resolve' | 'dismiss' | 'comment'
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState(indicator.title);
  const [desc, setDesc] = useState(indicator.description);
  const [status, setStatus] = useState(indicator.status);
  const [busy, setBusy] = useState(false);

  const callAction = async (kind, body) => {
    try {
      const path = `/screenings/${screeningId}/risk-indicators/${indicator.id}/${kind === "review" ? "review" : kind === "resolve" ? "resolve" : kind === "dismiss" ? "dismiss" : "comments"}`;
      const payload = kind === "resolve" ? { resolution_notes: body }
                    : kind === "dismiss" ? { dismissal_reason: body }
                    : kind === "comment" ? { body }
                    : {};
      await apiClient.post(path, payload);
      toast.success({
        review: "Marked Reviewed", resolve: "Marked Resolved",
        dismiss: "Indicator Dismissed", comment: "Comment added",
      }[kind]);
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed"); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await apiClient.patch(`/screenings/${screeningId}/risk-indicators/${indicator.id}`, {
        title: title.trim(), description: desc.trim(), status,
      });
      toast.success("Risk indicator updated");
      setEditing(false);
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove this risk indicator?")) return;
    try {
      await apiClient.delete(`/screenings/${screeningId}/risk-indicators/${indicator.id}`);
      toast.success("Removed");
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Remove failed"); }
  };

  const tone = STATUS_TONE[indicator.status] || STATUS_TONE.open;

  if (editing) {
    return (
      <li className="bg-slate-50 ring-1 ring-slate-200 rounded-md p-4 space-y-3" data-testid={`risk-indicator-editor-${indicator.id}`}>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="h-10 rounded-md" placeholder="Indicator title" />
        <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} className="rounded-md text-sm" placeholder="Observation details — what was found and why it may warrant review" />
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 rounded-md w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_TONE).map(([v, t]) => (
                <SelectItem key={v} value={v}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-2 ml-auto">
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={busy}>
              <X className="h-3.5 w-3.5 mr-1" /> Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={busy || !title.trim() || !desc.trim()} className="bg-slate-900" data-testid={`risk-indicator-save-${indicator.id}`}>
              <Check className="h-3.5 w-3.5 mr-1" /> Save
            </Button>
          </div>
        </div>
      </li>
    );
  }

  return (
    <li
      className={`bg-white ring-1 ring-slate-200 rounded-md p-5 border-l-4 ${tone.accent} shadow-sm hover:shadow-md transition-shadow`}
      data-testid={`risk-indicator-${indicator.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <ShieldAlert className={`h-4 w-4 ${
              indicator.status === "resolved" ? "text-emerald-500"
              : indicator.status === "dismissed" ? "text-slate-400"
              : indicator.status === "open" ? "text-amber-600"
              : "text-blue-600"
            }`} />
            <div className="font-semibold text-slate-900 text-base">{indicator.title}</div>
            {indicator.severity && SEVERITY_TONE[indicator.severity] && (
              <Badge variant="outline"
                     className={`text-[10px] uppercase tracking-wider font-bold ring-1 ring-inset ${SEVERITY_TONE[indicator.severity].cls}`}
                     data-testid={`risk-indicator-severity-${indicator.id}`}>
                {SEVERITY_TONE[indicator.severity].label}
              </Badge>
            )}
            <Badge variant="outline" className={`text-[10px] uppercase tracking-wider font-bold ${tone.cls}`}>{tone.label}</Badge>
          </div>
          <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap break-words leading-relaxed">{indicator.description}</p>
          <div className="text-[10px] font-mono text-slate-500 mt-2 flex items-center gap-3 flex-wrap">
            <span>Identified {fmtDate(indicator.identified_at)}</span>
            {indicator.reviewer_name && <span>· Reviewer: {indicator.reviewer_name}</span>}
          </div>
          {indicator.status === "resolved" && indicator.resolution_notes && (
            <div className="mt-3 border-l-2 border-emerald-400 pl-3 bg-emerald-50/50 py-1.5 pr-2 rounded-sm" data-testid={`risk-indicator-resolution-${indicator.id}`}>
              <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Resolved</div>
              <div className="text-sm text-slate-800 mt-1 whitespace-pre-wrap break-words">{indicator.resolution_notes}</div>
              <div className="text-[10px] font-mono text-slate-500 mt-1">{indicator.resolved_by_name} · {fmtDate(indicator.resolved_at)}</div>
            </div>
          )}
          {indicator.status === "dismissed" && indicator.dismissal_reason && (
            <div className="mt-3 border-l-2 border-slate-400 pl-3 bg-slate-50 py-1.5 pr-2 rounded-sm" data-testid={`risk-indicator-dismissal-${indicator.id}`}>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-700">Dismissed</div>
              <div className="text-sm text-slate-800 mt-1 whitespace-pre-wrap break-words">{indicator.dismissal_reason}</div>
              <div className="text-[10px] font-mono text-slate-500 mt-1">{indicator.dismissed_by_name} · {fmtDate(indicator.dismissed_at)}</div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {indicator.status === "open" && (
            <ActionButton title="Mark Reviewed" icon={Eye} onClick={() => callAction("review")} testid={`risk-indicator-review-${indicator.id}`} />
          )}
          {indicator.status !== "resolved" && indicator.status !== "dismissed" && (
            <>
              <ActionButton title="Resolve" icon={ShieldCheck} tone="emerald" onClick={() => setActionForm("resolve")} testid={`risk-indicator-resolve-${indicator.id}`} />
              <ActionButton title="Dismiss" icon={MinusCircle} tone="slate" onClick={() => setActionForm("dismiss")} testid={`risk-indicator-dismiss-${indicator.id}`} />
            </>
          )}
          <ActionButton title="Add comment" icon={MessageSquarePlus} onClick={() => setActionForm("comment")} testid={`risk-indicator-comment-${indicator.id}`} />
          {isStaff && (
            <>
              <ActionButton title="Edit" icon={Pencil} onClick={() => setEditing(true)} testid={`risk-indicator-edit-${indicator.id}`} />
              <ActionButton title="Remove" icon={Trash2} tone="red" onClick={remove} testid={`risk-indicator-remove-${indicator.id}`} />
            </>
          )}
        </div>
      </div>

      {actionForm && (
        <ActionForm mode={actionForm} onCancel={() => setActionForm(null)}
                    onSubmit={async (body) => { await callAction(actionForm, body); setActionForm(null); }} />
      )}

      {(indicator.events || []).length > 0 && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="text-[10px] tracking-widest uppercase font-semibold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
            data-testid={`risk-indicator-timeline-toggle-${indicator.id}`}
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            Activity · {indicator.events.length}
          </button>
          {expanded && (
            <ul className="mt-2 space-y-2" data-testid={`risk-indicator-timeline-${indicator.id}`}>
              {indicator.events.slice().reverse().map((ev) => (
                <li key={ev.id} className="text-xs text-slate-700">
                  <span className="font-semibold uppercase tracking-wider text-[9px] mr-1.5 text-slate-500">{ev.type}</span>
                  <span className="font-medium">{ev.actor_name}</span>
                  <span className="text-slate-400"> · {fmtDate(ev.at)}</span>
                  {ev.body && <div className="text-slate-700 mt-0.5 whitespace-pre-wrap break-words pl-3 border-l border-slate-200">{ev.body}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

function ActionButton({ title, icon: Icon, tone, onClick, testid }) {
  const toneCls = tone === "red" ? "text-red-600" : tone === "emerald" ? "text-emerald-600"
    : tone === "slate" ? "text-slate-500" : "text-slate-700";
  return (
    <Button size="sm" variant="ghost" className={`h-7 w-7 p-0 ${toneCls}`} onClick={onClick} title={title} data-testid={testid}>
      <Icon className="h-3.5 w-3.5" />
    </Button>
  );
}

function ActionForm({ mode, onCancel, onSubmit }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const labels = {
    resolve: { title: "Resolve — what was done?", placeholder: "Additional documents received / income clarified / employer confirmed / etc.", btn: "Mark Resolved" },
    dismiss: { title: "Dismiss — why doesn't this affect your decision?", placeholder: "Reason for dismissal", btn: "Dismiss Indicator" },
    comment: { title: "Add a comment", placeholder: "Note for the file…", btn: "Add Comment" },
  }[mode];
  const submit = async () => { setBusy(true); try { await onSubmit(body.trim()); } finally { setBusy(false); } };
  return (
    <div className="mt-3 bg-slate-50 ring-1 ring-slate-200 rounded-md p-3 space-y-2" data-testid={`risk-indicator-action-form-${mode}`}>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">{labels.title}</Label>
      <Textarea rows={3} maxLength={2000} value={body} onChange={(e) => setBody(e.target.value)} placeholder={labels.placeholder}
                className="rounded-md text-sm" data-testid={`risk-indicator-action-input-${mode}`} />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button size="sm" onClick={submit} disabled={busy || body.trim().length < 3} className="bg-slate-900" data-testid={`risk-indicator-action-submit-${mode}`}>
          {busy ? "Saving…" : labels.btn}
        </Button>
      </div>
    </div>
  );
}

function AddForm({ screeningId, onClose, onAdded }) {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [status, setStatus] = useState("open");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/screenings/${screeningId}/risk-indicators`, {
        title: title.trim(), description: desc.trim(), status,
      });
      toast.success("Risk indicator added");
      onAdded();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not add"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-slate-50 ring-1 ring-slate-200 rounded-md p-4 space-y-3" data-testid="risk-indicator-add-form">
      <div>
        <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">Indicator title</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Income discrepancy identified"
               className="h-10 rounded-md mt-1.5" maxLength={200} data-testid="risk-indicator-add-title" />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">Description</Label>
        <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} maxLength={2000}
                  placeholder="Observation details — what was found and why it may warrant review (e.g. 'Pay stub income $5,200/mo differs from application $6,800/mo')"
                  className="mt-1.5 rounded-md text-sm" data-testid="risk-indicator-add-description" />
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 rounded-md w-40" data-testid="risk-indicator-add-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(STATUS_TONE).map(([v, t]) => (
              <SelectItem key={v} value={v}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex gap-2 ml-auto">
          <Button size="sm" variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy || !title.trim() || !desc.trim()} className="bg-slate-900" data-testid="risk-indicator-add-submit">
            {busy ? "Adding…" : "Add indicator"}
          </Button>
        </div>
      </div>
    </div>
  );
}
